import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChartTheme } from "@/hooks/useChartTheme";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MonthPicker } from "@/components/husholdning/MonthPicker";
import { EntryDialog } from "@/components/husholdning/EntryDialog";
import { ChoosePlanState } from "@/components/husholdning/ChoosePlanState";
import { ReportDownloadMenu } from "@/components/husholdning/ReportDownloadMenu";
import { formatKr, formatMonth } from "@/lib/format";
import { currentMonth, isValidMonth, budgetPct, HOUSEHOLD_SOURCES, HOUSEHOLD_HOME, planTerms, capitalize } from "@/lib/householdConstants";
import { cn } from "@/lib/utils";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { ArrowDownLeft, ArrowUpRight, Scale, Wallet, ChevronDown, Plus, AlertTriangle, Tags, Info, House, PiggyBank, Receipt, BookOpenText, FileText, FileSpreadsheet } from "lucide-react";

const Kpi = ({ label, value, sub, icon: Icon, tone, testId, to }) => {
  const navigate = useNavigate();
  return (
    <Card className={cn("shadow-none", to && "cursor-pointer card-lift")} onClick={to ? () => navigate(to) : undefined} data-testid={testId}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className={cn("font-heading text-2xl font-semibold tnum mt-0.5 whitespace-nowrap", tone)} data-testid={`${testId}-value`}>{value}</p>
            {sub ? <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p> : null}
          </div>
          <span className="h-9 w-9 rounded-xl bg-accent text-accent-foreground flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
        </div>
      </CardContent>
    </Card>
  );
};

const diffLabel = (now, prev, invert = false) => {
  if (!prev) return "Ingen data forrige måned";
  const d = now - prev;
  const pct = Math.round((d / Math.abs(prev)) * 100);
  const up = d > 0;
  const good = invert ? !up : up;
  return `${up ? "+" : ""}${formatKr(d)} (${up ? "+" : ""}${pct} %) mot forrige måned${d === 0 ? "" : good ? " ✓" : ""}`;
};

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{formatMonth(label)}</p>
      <p className="text-success">Inntekt: {formatKr(row.income)}</p>
      <p className="text-flag">Utgift: {formatKr(row.expense)}</p>
      <p className="text-muted-foreground">Resultat: {formatKr(row.result)}</p>
    </div>
  );
};

/** Regnskap → Oversikt: måned, KPI, trend, grupper med undergrupper/konti og budsjett. Terminologi følger virksomhetens kontoplan. */
export default function HouseholdOverviewPage() {
  const { activeWorkspace, canWrite, actAs } = useAuth();
  const chart = useChartTheme();
  const [params, setParams] = useSearchParams();
  const month = isValidMonth(params.get("mnd")) ? params.get("mnd") : currentMonth();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  const [entryOpen, setEntryOpen] = useState(false);

  const load = useCallback(() => {
    setError("");
    setData(null);
    Promise.all([api.get("/household/overview", { params: { month } }), api.get("/household/plan")])
      .then(([o, p]) => { setData(o.data); setPlan(p.data); })
      .catch((e) => setError(errorMessage(e)));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const setMonth = (m) => { const p = new URLSearchParams(params); if (m === currentMonth()) p.delete("mnd"); else p.set("mnd", m); setParams(p, { replace: true }); };

  const groupsWithData = useMemo(() => (data?.groups ? data.groups.filter((g) => g.total !== 0 || g.budget) : []), [data]);
  const groupsEmpty = useMemo(() => (data?.groups ? data.groups.filter((g) => g.total === 0 && !g.budget) : []), [data]);
  const maxGroup = useMemo(() => Math.max(1, ...groupsWithData.map((g) => Math.max(g.total, g.budget || 0))), [groupsWithData]);
  const wsName = actAs ? actAs.owner_name : activeWorkspace?.name;
  const t = planTerms(data?.standard || plan?.standard);
  const tx = `${HOUSEHOLD_HOME}/transaksjoner`;

  if (error) return <EmptyState icon={AlertTriangle} title="Kunne ikke laste regnskapet" description={error} actionLabel="Prøv igjen" onAction={load} testId="household-error" />;

  // Virksomheten har ikke valgt kontoplan ennå (brukerbeslutning: ingen plan settes automatisk)
  if (data?.needs_plan) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Regnskap" title="Oversikt" description={`Regnskap for ${wsName || "virksomheten"} – regninger som utgifter, utstedte fakturaer som inntekter og manuelle poster.`} />
        <ChoosePlanState data={data} onChosen={load} />
      </div>
    );
  }

  const hasAny = data && (data.count > 0 || data.pending?.count > 0);
  const savingsRate = data && data.income_total > 0 ? Math.round((data.result / data.income_total) * 100) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t.eyebrow}
        title="Oversikt"
        description={`Inntekter og ${t.expenses.toLowerCase()} for ${wsName || (t.isHousehold ? "husholdningen" : "virksomheten")} – regninger, utstedte fakturaer og manuelle poster ført etter ${t.planName}.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MonthPicker month={month} onChange={setMonth} />
            <ReportDownloadMenu
              disabled={!data}
              heading={`${formatMonth(month)} · ${t.short}`}
              testId="household-overview-download"
              items={[
                { key: "pdf", label: `Månedsrapport ${formatMonth(month).toLowerCase()} (PDF)`, hint: "Resultatoppstilling mot forrige måned og budsjett", icon: FileText, url: "/household/report.pdf", params: { month }, filename: `maanedsrapport_${t.key}_${month}.pdf` },
                { key: "pdf-year", label: `Årsrapport ${month.slice(0, 4)} (PDF)`, hint: "Hele året med budsjett, i fjor og måned for måned", icon: FileText, url: "/household/report.pdf", params: { year: Number(month.slice(0, 4)) }, filename: `aarsrapport_${t.key}_${month.slice(0, 4)}.pdf` },
                { separator: true },
                { key: "csv-month", label: `Alle poster ${formatMonth(month).toLowerCase()} (CSV)`, hint: "Én rad per regning, faktura og manuell post", icon: FileSpreadsheet, url: "/household/export", params: { month }, filename: `regnskap_${t.key}_${month}.csv` },
              ]}
            />
            {canWrite ? <Button onClick={() => setEntryOpen(true)} data-testid="household-add-entry-button"><Plus className="h-4 w-4 mr-2" /> Ny post</Button> : null}
          </div>
        }
      />

      {/* KPI */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="household-kpis">
        {!data ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />) : (
          <>
            <Kpi label="Inntekter" value={formatKr(data.income_total)} sub={diffLabel(data.income_total, data.prev.income_total)} icon={ArrowDownLeft} tone="text-success" testId="household-kpi-income" to={`${tx}?mnd=${month}&type=inntekt`} />
            <Kpi label={t.expenses} value={formatKr(data.expense_total)} sub={diffLabel(data.expense_total, data.prev.expense_total, true)} icon={ArrowUpRight} tone="text-flag" testId="household-kpi-expense" to={`${tx}?mnd=${month}&type=utgift`} />
            <Kpi label="Resultat" value={formatKr(data.result)} sub={savingsRate !== null ? `${savingsRate} % av inntekten ${data.result >= 0 ? "til overs" : "i underskudd"}` : `Inntekt minus ${t.expenses.toLowerCase()}`} icon={data.result >= 0 ? PiggyBank : Scale} tone={data.result < 0 ? "text-flag" : ""} testId="household-kpi-result" />
            <Kpi label="Budsjett" value={data.budget_total ? `${Math.round((data.expense_total / data.budget_total) * 100)} %` : "–"} sub={data.budget_total ? `${formatKr(data.expense_total)} av ${formatKr(data.budget_total)} brukt` : `Sett månedsbudsjett under ${t.isHousehold ? "Kategoriplan" : "Kontoplan"}`} icon={Wallet} tone={data.budget_total && data.expense_total > data.budget_total ? "text-flag" : ""} testId="household-kpi-budget" to={`${HOUSEHOLD_HOME}/kategorier`} />
          </>
        )}
      </div>

      {/* Fakturaer som venter på inntektskategori (holdes utenfor summene) */}
      {data && data.pending?.count > 0 ? (
        <Alert data-testid="household-pending-alert">
          <Receipt className="h-4 w-4" />
          <AlertTitle>{data.pending.count} {data.pending.count === 1 ? "faktura venter" : "fakturadokumenter venter"} på {t.income} ({formatKr(data.pending.total)})</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Fakturaer telles først når du har valgt {t.income} – til da holdes de utenfor inntektene.</span>
            <Link to={`${tx}?mnd=${month}&kategori=venter`} className="underline font-medium" data-testid="household-pending-link">Velg kategori nå</Link>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Regninger uten kategori */}
      {data && data.unmapped.count > 0 ? (
        <Alert data-testid="household-unmapped-alert">
          <Tags className="h-4 w-4" />
          <AlertTitle>{data.unmapped.count} {data.unmapped.count === 1 ? "post" : "poster"} ukategorisert ({formatKr(data.unmapped.total)})</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Regninger uten koblet kategori telles i totalen som «Ukategorisert», men ikke i {t.group}ne.</span>
            <Link to={`${tx}?mnd=${month}&kategori=ingen`} className="underline font-medium" data-testid="household-unmapped-link">Vis postene</Link>
            {plan?.unmapped_regninger_categories > 0 ? <Link to={`${HOUSEHOLD_HOME}/kategorier#kobling`} className="underline font-medium" data-testid="household-mapping-link">Koble {plan.unmapped_regninger_categories} Regninger-{plan.unmapped_regninger_categories === 1 ? "kategori" : "kategorier"}</Link> : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {data && !hasAny ? (
        <EmptyState icon={t.isHousehold ? House : BookOpenText} title={`Ingen poster i ${formatMonth(month).toLowerCase()}`} description={`Regninger med forfall denne måneden og fakturaer utstedt denne måneden dukker opp automatisk. ${t.isHousehold ? "Legg til lønn, dagligvarer og annet som manuelle poster." : "Legg til lønn, kontantkjøp og annet som manuelle poster."}`} actionLabel={canWrite ? "Legg til post" : undefined} onAction={canWrite ? () => setEntryOpen(true) : undefined} testId="household-empty" />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Trend */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Siste 12 måneder</CardTitle>
            <CardDescription>Inntekter og {t.expenses.toLowerCase()} per måned. Regninger telles i forfallsmåneden, fakturaer i fakturamåneden når {t.income} er valgt.</CardDescription>
          </CardHeader>
          <CardContent>
            {!data ? <Skeleton className="h-64" /> : (
              <div className="h-64" data-testid="household-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.trend} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} barGap={2}>
                    <CartesianGrid stroke={chart.grid} vertical={false} />
                    <XAxis dataKey="month" tickFormatter={(m) => formatMonth(m, true)} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} interval={1} />
                    <YAxis tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                    <RTooltip content={<ChartTip />} cursor={{ fill: chart.cursor }} />
                    <ReferenceLine y={0} stroke={chart.grid} />
                    <Bar dataKey="income" name="Inntekt" fill={chart.paid} radius={[4, 4, 0, 0]} maxBarSize={22} />
                    <Bar dataKey="expense" name={t.expenses} fill={chart.primary} radius={[4, 4, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {data ? (
              <ul className="flex flex-wrap justify-center gap-5 pt-2 text-sm">
                <li className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: chart.paid }} />Inntekt</li>
                <li className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: chart.primary }} />{t.expenses}</li>
              </ul>
            ) : null}
          </CardContent>
        </Card>

        {/* Kilder + inntekter */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Hvor kommer tallene fra?</CardTitle>
            <CardDescription>{formatMonth(month)} · {t.planName}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!data ? <Skeleton className="h-40" /> : (
              <>
                <ul className="space-y-2" data-testid="household-sources">
                  {Object.entries(HOUSEHOLD_SOURCES).map(([k, s]) => (
                    <li key={k} className="flex items-center justify-between gap-3 text-sm">
                      <span className="flex items-center gap-2 min-w-0"><s.icon className="h-4 w-4 text-muted-foreground shrink-0" /><span className="truncate">{s.label}{k === "faktura" ? " (inntekt)" : k === "regning" ? " (utgift)" : ""}</span>
                        <Tooltip><TooltipTrigger asChild><button type="button" className="text-muted-foreground" aria-label="Forklaring"><Info className="h-3.5 w-3.5" /></button></TooltipTrigger><TooltipContent className="max-w-xs">{s.help}</TooltipContent></Tooltip>
                      </span>
                      <span className="font-medium tnum whitespace-nowrap" data-testid={`household-source-${k}`}>{formatKr(data.sources[k] || 0)}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t pt-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Inntekter per {t.income}</p>
                  {data.income.filter((c) => c.total !== 0).length === 0 ? <p className="text-sm text-muted-foreground">Ingen inntekter registrert.</p> : (
                    <ul className="space-y-1.5" data-testid="household-income-list">
                      {data.income.filter((c) => c.total !== 0).map((c) => (
                        <li key={c.id}>
                          <Link to={`${tx}?mnd=${month}&kategori=${c.id}`} className="flex items-center justify-between gap-3 text-sm hover:underline" data-testid={`household-income-${c.code}`}>
                            <span className="flex items-center gap-2 min-w-0"><span className="h-2 w-2 rounded-full shrink-0" style={{ background: c.color }} /><span className="truncate"><span className="text-muted-foreground tnum mr-1">{c.code}</span>{c.name}</span></span>
                            <span className="font-medium tnum whitespace-nowrap">{formatKr(c.total)}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Utgifter/kostnader per gruppe */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">{t.expenses} per {t.group}</CardTitle>
              <CardDescription>{t.isHousehold ? "COICOP-grupper med undergrupper" : "NS 4102-kontogrupper med konti"}. Klikk en gruppe for detaljer{data?.budget_total ? " – budsjett vises som markør" : ""}.</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild><Link to={`${HOUSEHOLD_HOME}/aar?aar=${month.slice(0, 4)}`} data-testid="household-year-link">Se hele året</Link></Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!data ? <div className="p-6 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div> : groupsWithData.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-muted-foreground" data-testid="household-groups-empty">Ingen {t.expenses.toLowerCase()} registrert denne måneden.</p>
          ) : (
            <ul className="divide-y" data-testid="household-groups">
              {groupsWithData.sort((a, b) => b.total - a.total).map((g) => {
                const pct = budgetPct(g.total, g.budget);
                const over = pct !== null && pct > 100;
                const open = !!openGroups[g.id];
                return (
                  <li key={g.id} data-testid={`household-group-${g.code}`}>
                    <Collapsible open={open} onOpenChange={(o) => setOpenGroups((s) => ({ ...s, [g.id]: o }))}>
                      <CollapsibleTrigger asChild>
                        <button type="button" className="w-full text-left px-4 sm:px-6 py-3 hover:bg-secondary/50 transition-colors" data-testid={`household-group-toggle-${g.code}`}>
                          <div className="flex items-center gap-3">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: g.color }} />
                            <span className="text-xs text-muted-foreground tnum w-6 shrink-0">{g.code}</span>
                            <span className="font-medium flex-1 min-w-0 truncate">{g.name}{g.outside ? <span className="ml-2 text-[11px] font-normal text-muted-foreground">{t.outside}</span> : null}</span>
                            <span className="text-xs text-muted-foreground hidden sm:inline">{g.count} {g.count === 1 ? "post" : "poster"}</span>
                            <span className={cn("font-medium tnum whitespace-nowrap", over && "text-flag")} data-testid={`household-group-total-${g.code}`}>{formatKr(g.total)}</span>
                            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
                          </div>
                          <div className="mt-2 ml-[22px] relative h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div className={cn("absolute inset-y-0 left-0 rounded-full", over ? "bg-[#B42318]" : "")} style={{ width: `${Math.min(100, (g.total / maxGroup) * 100)}%`, background: over ? undefined : g.color }} />
                            {g.budget ? <div className="absolute inset-y-0 w-0.5 bg-foreground/60" style={{ left: `${Math.min(100, (g.budget / maxGroup) * 100)}%` }} title={`Budsjett ${formatKr(g.budget)}`} /> : null}
                          </div>
                          {g.budget ? <p className={cn("mt-1 ml-[22px] text-[11px]", over ? "text-flag" : "text-muted-foreground")} data-testid={`household-group-budget-${g.code}`}>{pct} % av budsjett {formatKr(g.budget)}{over ? ` · ${formatKr(g.total - g.budget)} over` : ""}</p> : null}
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <ul className="pb-3 px-4 sm:px-6" data-testid={`household-group-children-${g.code}`}>
                          {g.children.filter((c) => c.total !== 0 || c.budget).map((c) => {
                            const cpct = budgetPct(c.total, c.budget);
                            return (
                              <li key={c.id}>
                                <Link to={`${tx}?mnd=${month}&kategori=${c.id}`} className="flex items-center gap-3 py-1.5 pl-[22px] text-sm hover:underline" data-testid={`household-child-${c.code}`}>
                                  <span className="text-xs text-muted-foreground tnum w-9 shrink-0">{c.code}</span>
                                  <span className="flex-1 min-w-0 truncate">{c.name}</span>
                                  {c.budget ? <span className={cn("text-xs", cpct > 100 ? "text-flag" : "text-muted-foreground")}>{cpct} % av {formatKr(c.budget)}</span> : null}
                                  <span className="tnum whitespace-nowrap">{formatKr(c.total)}</span>
                                </Link>
                              </li>
                            );
                          })}
                          {g.children.every((c) => c.total === 0 && !c.budget) ? <li className="pl-[22px] text-sm text-muted-foreground py-1.5">Ført direkte på {t.group}n.</li> : null}
                          <li className="pl-[22px] pt-1"><Link to={`${tx}?mnd=${month}&kategori=${g.id}`} className="text-xs underline text-muted-foreground" data-testid={`household-group-all-${g.code}`}>Alle poster i {g.name}</Link></li>
                        </ul>
                      </CollapsibleContent>
                    </Collapsible>
                  </li>
                );
              })}
            </ul>
          )}
          {data && groupsEmpty.length > 0 && groupsWithData.length > 0 ? (
            <p className="px-4 sm:px-6 py-3 border-t text-xs text-muted-foreground" data-testid="household-groups-zero">{capitalize(t.group)}r uten poster denne måneden: {groupsEmpty.map((g) => `${g.code} ${g.name}`).join(" · ")}</p>
          ) : null}
        </CardContent>
      </Card>

      <EntryDialog open={entryOpen} onOpenChange={setEntryOpen} plan={plan} defaults={{ date: month === currentMonth() ? undefined : `${month}-01` }} onSaved={load} />
    </div>
  );
}
